// src/pages/AgenticWorkflowDetail/components/UploadContactsModal/UploadContactsModal.tsx
import React, { useState, useRef, ChangeEvent } from 'react';
import { Info, Upload, FileText } from 'lucide-react';
import { Modal, Button, Input, Table } from 'common/components';
import { useUploadWorkflowContacts } from 'services/hooks/mutations';
import { UploadWorkflowContact } from 'services/api/postUploadWorkflowContacts';
import { NotificationModalState } from 'pages/AgenticWorkflowDetail/AgenticWorkflowDetail.types';
import { parseCSV } from './utils';

import styles from './UploadContactsModal.module.scss';

export interface UploadContactsModalProps {
    isOpen: boolean;
    onClose: () => void;
    workflowTitle: string;
    workflowId: string | undefined;
    onUploadSuccess: () => void;
    onNotification: (notification: NotificationModalState) => void;
}

export const UploadContactsModal: React.FC<UploadContactsModalProps> = ({
    isOpen,
    onClose,
    workflowTitle,
    workflowId,
    onUploadSuccess,
    onNotification,
}) => {
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { uploadWorkflowContacts, isPending: isUploading } = useUploadWorkflowContacts();
    const handleClose = (): void => {
        setUploadFile(null);
        onClose();
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
        if (e.target.files?.[0]) {
            setUploadFile(e.target.files[0]);
        }
    };

    const triggerFileInput = (): void => {
        fileInputRef.current?.click();
    };

    const handleUploadWorkflowContacts = (payload: { workflowId: string; contacts: UploadWorkflowContact[] }) =>
        uploadWorkflowContacts(payload, {
            onSuccess: () => {
                onNotification({
                    isOpen: true,
                    title: 'Upload Successful',
                    message: 'Contacts have been uploaded successfully.',
                    type: 'success',
                    buttons: [],
                });
                handleClose();
                onUploadSuccess();
            },
            onError: (error) => {
                onNotification({
                    isOpen: true,
                    title: 'Upload Error',
                    message: 'Error uploading contacts: ' + ((error as Error).message || 'Unknown error'),
                    type: 'error',
                    buttons: [],
                });
            },
        });

    const handleUploadFile = async (): Promise<void> => {
        if (!uploadFile) {
            onNotification({
                isOpen: true,
                title: 'Upload Required',
                message: 'Please select a CSV file to upload.',
                type: 'error',
                buttons: [],
            });
            return;
        }

        try {
            const fileReader = new FileReader();
            fileReader.onload = async (e) => {
                const csvText = e.target?.result as string;
                const lines = csvText.split('\n');

                const { contacts, invalidPhoneCount, invalidPhonesData, missingHeaders } = parseCSV(lines);

                if (missingHeaders.length) {
                    onNotification({
                        isOpen: true,
                        title: 'Invalid CSV Format',
                        message: `CSV is missing required headers: ${missingHeaders.join(', ')}`,
                        type: 'error',
                        buttons: [],
                    });
                    return;
                }

                const payload = {
                    workflowId: workflowId || '',
                    contacts,
                };

                if (invalidPhoneCount > 0) {
                    onNotification({
                        isOpen: true,
                        title: 'Invalid Phone Numbers Found',
                        message: `Found ${invalidPhoneCount} invalid phone number(s). Phone numbers must be US format: +1XXXXXXXXXX (11 digits total).`,
                        type: 'warning',
                        body: (
                            <div className='mt-md'>
                                <Table
                                    variant='secondary'
                                    data={invalidPhonesData}
                                    columns={[
                                        { key: 'name', title: 'Name', dataIndex: 'name' },
                                        { key: 'phoneNumber', title: 'Phone Number', dataIndex: 'phoneNumber' },
                                    ]}
                                />
                                <p className='mt-xl'>
                                    Press 'Continue' to ignore the invalid phone numbers and upload the remaining
                                    contacts.
                                </p>
                            </div>
                        ),
                        buttons: [
                            {
                                label: 'Cancel',
                                variant: 'outline-secondary',
                                onClick: handleClose,
                            },
                            {
                                label: 'Continue',
                                onClick: async () => {
                                    onNotification({
                                        isOpen: false,
                                        title: '',
                                        message: '',
                                    });
                                    await handleUploadWorkflowContacts(payload);
                                },
                            },
                        ],
                    });
                    return;
                }

                await handleUploadWorkflowContacts(payload);
            };

            fileReader.readAsText(uploadFile);
        } catch (err) {
            console.error('Error reading file:', err);
            onNotification({
                isOpen: true,
                title: 'File Error',
                message: 'Error reading file: ' + ((err as Error).message || 'Unknown error'),
                type: 'error',
                buttons: [],
            });
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={`Upload Contacts for: ${workflowTitle}`}
            size='md'
            showDefaultFooter={false}
            footer={
                <div className={styles.footer}>
                    <Button variant='outline-secondary' onClick={handleClose} disabled={isUploading}>
                        Cancel
                    </Button>
                    <Button variant='primary' icon={Upload} onClick={handleUploadFile} isLoading={isUploading}>
                        Upload
                    </Button>
                </div>
            }
        >
            <div className={styles.uploadInstructions}>
                <div className={styles.uploadInstructionsHeader}>
                    <h4>CSV File Requirements:</h4>

                    <div className={styles.tooltip}>
                        <Info size={16} color='#9b51e0' />

                        <div className={styles.uploadNotes}>
                            <strong>Notes:</strong>
                            <ul>
                                <li>Phone numbers must be valid US numbers starting with +1</li>
                                <li>Remove any spaces, dashes, or parentheses from phone numbers</li>
                                <li>Each row represents one contact</li>
                                <li>First row must contain the column headers</li>
                            </ul>
                        </div>
                    </div>
                </div>
                <ul>
                    <li>
                        <strong>Required columns:</strong> firstName, lastName, phoneNumber
                    </li>
                    <li>
                        <strong>Optional columns:</strong> crmStatus
                    </li>
                    <li>
                        <strong>Phone number:</strong> Valid US numbers with 11 digits including country code
                    </li>
                    <li>
                        <strong>Example:</strong> +1 555 123 4567 (Country code + Area Code + Phone number)
                    </li>
                    <li>
                        <strong>Format:</strong> CSV (Comma Separated Values)
                    </li>
                </ul>
            </div>
            <div className={styles.fileInputContainer}>
                <input
                    type='file'
                    ref={fileInputRef}
                    accept='.csv'
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                <Input
                    type='text'
                    value={uploadFile ? uploadFile.name : ''}
                    placeholder='No file selected'
                    readOnly
                    onClick={triggerFileInput}
                    className={styles.fileNameInput}
                />
                <Button variant='outline-primary' onClick={triggerFileInput} type='button' icon={FileText}>
                    Choose CSV
                </Button>
            </div>
        </Modal>
    );
};
